import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { DbTrendArrowProps, TrendType } from './DbTrendArrow.types';
import { styles, defaultColors } from './DbTrendArrow.styles';

/**
 * 上升箭头组件
 */
const UpArrow: React.FC<{ size: number; color: string }> = ({ size, color }) => (
    <View style={styles.arrow}>
        <Text style={{ fontSize: size, color, lineHeight: size + 2 }}>▲</Text>
    </View>
);

/**
 * 下降箭头组件
 */
const DownArrow: React.FC<{ size: number; color: string }> = ({ size, color }) => (
    <View style={styles.arrow}>
        <Text style={{ fontSize: size, color, lineHeight: size + 2 }}>▼</Text>
    </View>
);

/**
 * DbTrendArrow 趋势箭头组件
 * 
 * 用于展示带有上涨/下跌趋势的数值
 * 
 * @example
 * // 基础用法 - 显示上涨和下跌
 * <DbTrendArrow value="1.00%" upValue="0.25%" downValue="0.25%" />
 * 
 * @example
 * // 只显示单个趋势
 * <DbTrendArrow value="10.24%" trend="up" upValue="0.25%" />
 * 
 * @example
 * // 自定义颜色
 * <DbTrendArrow 
 *   value="5.50%" 
 *   upValue="1.2%" 
 *   downValue="0.8%"
 *   upColor="#E53935"
 *   downColor="#43A047" 
 * />
 */
export const DbTrendArrow: React.FC<DbTrendArrowProps> = ({
    value,
    upValue,
    downValue,
    trend,
    upColor = defaultColors.up,
    downColor = defaultColors.down,
    neutralColor = defaultColors.neutral,
    valueColor,
    valueFontSize = 16,
    trendFontSize = 14,
    arrowSize = 10,
    spacing = 8,
    trendSpacing = 8,
    showArrow = true,
    style,
    valueStyle,
    trendStyle,
}) => {
    // 计算主要数值的颜色
    const computedValueColor = useMemo(() => {
        if (valueColor) return valueColor;
        if (trend === 'up') return upColor;
        if (trend === 'down') return downColor;
        return defaultColors.value;
    }, [valueColor, trend, upColor, downColor]);

    // 渲染单个趋势项
    const renderTrendItem = (
        trendValue: string | number | undefined,
        type: TrendType,
        isLast: boolean = false
    ) => {
        if (trendValue === undefined) return null;

        const color = type === 'up' ? upColor : type === 'down' ? downColor : neutralColor;
        
        return (
            <View 
                style={[
                    styles.trendItem,
                    !isLast && { marginRight: trendSpacing }
                ]}
            >
                {showArrow && type === 'up' && <UpArrow size={arrowSize} color={color} />}
                {showArrow && type === 'down' && <DownArrow size={arrowSize} color={color} />}
                <Text 
                    style={[
                        styles.trendText, 
                        { fontSize: trendFontSize, color },
                        trendStyle
                    ]}
                >
                    {typeof trendValue === 'number' ? `${trendValue}%` : trendValue}
                </Text>
            </View>
        );
    };

    // 如果指定了单一趋势
    if (trend) {
        const trendValue = trend === 'up' ? upValue : downValue;
        const color = trend === 'up' ? upColor : trend === 'down' ? downColor : neutralColor;
        
        return (
            <View style={[styles.container, style]}>
                <Text 
                    style={[
                        styles.valueText,
                        { fontSize: valueFontSize, color: computedValueColor },
                        valueStyle
                    ]}
                >
                    {value}
                </Text>
                {trendValue !== undefined && (
                    <View style={[styles.trendContainer, { marginLeft: spacing }]}>
                        {showArrow && trend === 'up' && <UpArrow size={arrowSize} color={color} />}
                        {showArrow && trend === 'down' && <DownArrow size={arrowSize} color={color} />}
                        <Text 
                            style={[
                                styles.trendText, 
                                { fontSize: trendFontSize, color },
                                trendStyle
                            ]}
                        >
                            {typeof trendValue === 'number' ? `${trendValue}%` : trendValue}
                        </Text>
                    </View>
                )}
            </View>
        );
    }

    // 显示上涨和下跌
    return (
        <View style={[styles.container, style]}>
            <Text 
                style={[
                    styles.valueText,
                    { fontSize: valueFontSize, color: computedValueColor },
                    valueStyle
                ]}
            >
                {value}
            </Text>
            {(upValue !== undefined || downValue !== undefined) && (
                <View style={[styles.trendContainer, { marginLeft: spacing }]}>
                    {renderTrendItem(upValue, 'up', downValue === undefined)}
                    {renderTrendItem(downValue, 'down', true)}
                </View>
            )}
        </View>
    );
};

// 保持向后兼容的别名
export const TrendArrow = DbTrendArrow;

export default DbTrendArrow;
export type { DbTrendArrowProps, TrendType } from './DbTrendArrow.types';
